import app from 'flarum/common/app';
export { default as ArenaCard } from './models/ArenaCard';
export { default as ArenaBattle } from './models/ArenaBattle';

// ── Elemental constants ──────────────────────────────────────────────────────

export const ELEMENT_LABELS = {
  get fire() {
    return app.translator.trans('forumaker-arena.lib.elements.fire');
  },
  get wind() {
    return app.translator.trans('forumaker-arena.lib.elements.wind');
  },
  get lightning() {
    return app.translator.trans('forumaker-arena.lib.elements.lightning');
  },
  get earth() {
    return app.translator.trans('forumaker-arena.lib.elements.earth');
  },
  get water() {
    return app.translator.trans('forumaker-arena.lib.elements.water');
  }
};
export const ELEMENT_ICONS = {
  fire: 'fas fa-fire',
  wind: 'fas fa-wind',
  lightning: 'fas fa-bolt',
  earth: 'fas fa-mountain',
  water: 'fas fa-water'
};
export const ELEMENT_COLORS = {
  fire: '#A4243B',
  wind: '#57789E',
  lightning: '#7261A8',
  earth: '#8B635C',
  water: '#45698D'
};

// ── Bonus card constants ─────────────────────────────────────────────────────

export const BONUS_EFFECT_LABELS = {
  heal: 'Исцеление',
  shield: 'Щит',
  regeneration: 'Регенерация',
  poison: 'Яд',
  weaken: 'Ослабление',
  reflect: 'Отражение',
  mana_steal: 'Похищение',
  power_surge: 'Всплеск',
  debuff_remove: 'Очищение',
  overload: 'Выстрел',
  resonance: 'Резонанс',
  sacrifice: 'Жертва',
  swap_stats: 'Перестановка',
  random_effect: 'Рандом',
  amplify: 'Усиление',
  copy: 'Копирование',
  elemental_tide: 'Прилив',
  elemental_dominance: 'Превосходство',
  preparation: 'Подготовка',
  involution: 'Инволюция'
};
export const BONUS_EFFECT_ICONS = {
  heal: 'fas fa-heart',
  shield: 'fas fa-shield-alt',
  regeneration: 'fas fa-tint',
  poison: 'fas fa-skull-crossbones',
  weaken: 'fas fa-arrow-down',
  reflect: 'fas fa-undo-alt',
  mana_steal: 'fas fa-hand-holding-water',
  power_surge: 'fas fa-plus-circle',
  debuff_remove: 'fas fa-broom',
  overload: 'fas fa-crosshairs',
  resonance: 'fas fa-bolt',
  sacrifice: 'fas fa-heart-broken',
  swap_stats: 'fas fa-exchange-alt',
  random_effect: 'fas fa-dice',
  amplify: 'fas fa-angle-double-up',
  copy: 'fas fa-copy',
  elemental_tide: 'fas fa-wave-square',
  elemental_dominance: 'fas fa-crown',
  preparation: 'fas fa-piggy-bank',
  involution: 'fas fa-compress-arrows-alt'
};
export const BONUS_EFFECT_COLORS = {
  heal: '#80b77e',
  shield: '#19647E',
  regeneration: '#06b6d4',
  poison: '#60992D',
  weaken: '#fda371',
  reflect: '#FCB1A6',
  mana_steal: '#9b5de5',
  power_surge: '#FFA51F',
  debuff_remove: '#e2e8f0',
  overload: '#CC005F',
  resonance: '#FFD400',
  sacrifice: '#ec4899',
  swap_stats: '#14b8a6',
  random_effect: '#893168',
  amplify: '#f43f5e',
  copy: '#4C6EF5',
  elemental_tide: '#0EA5E9',
  elemental_dominance: '#7C3AED',
  preparation: '#10B981',
  involution: '#64748B'
};

// app.translator.trans() returns Mithril.Children, not a guaranteed plain
// string: when the translation has parameters, it comes back as an array of
// string parts (Mithril renders these fine directly as JSX children, with no
// separator). Handing that array to anything that expects a real string
// instead — an HTML attribute, confirm()/alert(), etc. — coerces it via
// Array.prototype.toString(), which inserts a stray "," between every part
// (e.g. "Steals ,2, mana", or "Delete deck «,123,»?"). Joining the array with
// '' instead yields the correct plain string, which is fine as long as the
// translation has no rich (HTML/vnode) content — true for every plain
// confirm()/alert()/attribute use in this extension.
export function transPlain(id, parameters) {
  const translated = app.translator.trans(id, parameters);
  return Array.isArray(translated) ? translated.join('') : String(translated);
}

// Effect descriptions live in locale (forumaker-arena.lib.bonus_effect_descriptions.*),
// already parametrized with ICU plural rules — see transPlain() above for why
// this can't just be app.translator.trans() directly.
export function formatBonusDescription(effect, value) {
  return transPlain("forumaker-arena.lib.bonus_effect_descriptions.".concat(effect), {
    value: value != null ? value : '?'
  });
}
export const BONUS_COLOR = '#6366f1'; // default color for bonus cards with no effect set

// ── Mutable settings (overridden at runtime from admin) ──────────────────────
export const ARENA_SETTINGS = {
  manaLabel: 'Мана'
};

// Card backs: a dynamic, admin-configurable list (name, image, win threshold).
// id 0 ("no card back") isn't stored here — it's always the available
// default. The array starts empty and is populated on the forum from
// app.forum.attribute('arenaCardBacks') (see forum/index.ts), via mutation
// (length=0 + push) rather than reassignment, so every module that imported
// this constant sees the updated data through the same reference.

export const ARENA_CARD_BACKS = [];

/** Card back image by id (0/null → no card back → null). */
export function getCardBackImage(id) {
  var _ARENA_CARD_BACKS$fin;
  if (!id) return null;
  return ((_ARENA_CARD_BACKS$fin = ARENA_CARD_BACKS.find(b => b.id === id)) == null ? void 0 : _ARENA_CARD_BACKS$fin.imageUrl) || null;
}

/** Card back name by id (0/null → no card back → null). */
export function getCardBackName(id) {
  var _ARENA_CARD_BACKS$fin2;
  if (!id) return null;
  return ((_ARENA_CARD_BACKS$fin2 = ARENA_CARD_BACKS.find(b => b.id === id)) == null ? void 0 : _ARENA_CARD_BACKS$fin2.name) || null;
}

// ── Contrast helper ──────────────────────────────────────────────────────────
// Returns '#fff' or 'rgba(0,0,0,0.82)' based on background brightness.
export function getContrastColor(hex) {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 135 ? 'rgba(0,0,0,0.82)' : '#fff';
}